// DeviceListTable – main devices table wrapper, wiring selection and layout.

import React, { JSX, useEffect, useMemo, useRef, useState } from 'react';
import {
    DeviceMasterRow,
    ExtraColumnKey,
    DeviceStatus
} from '../../../../types/DeviceListTypes';
import {
    TABLE_CLASS,
    TABLE_FRAME_CLASS,
    TABLE_HEAD_CELL_CLASS,
    TABLE_HEAD_STICKY_CLASS,
    TABLE_SCROLL_CLASS,
} from '../../shared/tableDesign';

import { DeviceTableRow, PROGRESS_STROBE_CSS } from './DeviceListTableRow';
import type { CommandDdbItem } from '../../../../types/Commands';
import { pairedFrontIdFromRear, pairedRearIdFromFront } from '../../../helpers/devicePairing';
import { isCommandActive } from '../utils/deviceListHelpers';
import { deviceMappingColumnLabel } from '../../../helpers/deviceMappingRows';
import { EXTRA_COLUMNS_HIDDEN_FROM_PICKER, extraColumnHeaderLabel } from '../utils/deviceTableExtraLabels';

// ---- Types ----

type Props = {
    sortedRows: DeviceMasterRow[];
    columns: string[];
    visibleMasterColumns: string[];
    visibleExtraColumns: ExtraColumnKey[];
    statusMap?: Record<string, DeviceStatus>;
    tlsMap?: Record<string, boolean>;
    selectedIds?: string[];
    allSelected?: boolean;
    latestCommandMap?: Record<string, CommandDdbItem>;
    onlineMap?: Record<string, boolean>;
    busyMap?: Record<string, boolean>;
    onToggleSelectAll: () => void;
    onToggleSelectOne: (deviceId: string) => void;
    onSetSelectedIds?: (ids: string[]) => void;
    onRowClick: (deviceId?: string) => void;
    onOpenDeviceDetails: (deviceId: string) => void;
    requestSort: (key: string) => void;
    getSortIndicator: (key: string) => string;
    onCenterMapOnLocation?: (lat: number, lng: number, deviceId?: string) => void;
    operatorScopedEventId?: string | null;
    lifecycleMap?: Record<string, Record<string, unknown> | null | undefined>;
    attributesMap?: Record<string, Record<string, unknown> | null | undefined>;
    validationFailureMap?: Record<string, string[]>;
    userIdToDisplayName?: Record<string, string>;
};

// ---- Helpers ----

/** Read busy flag from status (0 = not busy). */
function getBusyFlag(status?: DeviceStatus): number {
    const v = (status as any)?.busy;
    return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

// ---- Drag-select hook ----
//
// The drag overlay div is mutated directly (no React state) so mousemove never
// causes a component re-render.  Hit-testing converts all positions to
// scroll-container content coordinates so rows that have scrolled off-screen
// are still captured correctly.

function useDragSelect(params: {
    scrollRef: React.RefObject<HTMLDivElement | null>;
    onSetSelectedIds?: (ids: string[]) => void;
}) {
    const { scrollRef, onSetSelectedIds } = params;

    // Keep callback in a ref so the effect closure never goes stale.
    const setSelectedRef = useRef(onSetSelectedIds);
    useEffect(() => { setSelectedRef.current = onSetSelectedIds; }, [onSetSelectedIds]);

    const drag = useRef<{
        startX: number;
        startY: number;
        startScrollTop: number;
        curX: number;
        curY: number;
        active: boolean;
    } | null>(null);

    const rafRef   = useRef<number | null>(null);
    const mouseYRef = useRef(0);

    useEffect(() => {
        // Create the overlay div once.  All position updates go straight to the
        // DOM style – never through React state.
        const overlay = document.createElement('div');
        Object.assign(overlay.style, {
            position:      'fixed',
            display:       'none',
            border:        '1.5px solid rgba(59,130,246,0.75)',
            background:    'rgba(59,130,246,0.08)',
            pointerEvents: 'none',
            zIndex:        '9999',
            borderRadius:  '2px',
        });
        document.body.appendChild(overlay);

        function showOverlay(x1: number, y1: number, x2: number, y2: number) {
            overlay.style.left    = `${Math.min(x1, x2)}px`;
            overlay.style.top     = `${Math.min(y1, y2)}px`;
            overlay.style.width   = `${Math.abs(x2 - x1)}px`;
            overlay.style.height  = `${Math.abs(y2 - y1)}px`;
            overlay.style.display = 'block';
        }

        function hideOverlay() {
            overlay.style.display = 'none';
        }

        // Convert both the drag anchor and the current cursor to content
        // coordinates, then check which rows fall inside that range.
        // This correctly selects rows that have scrolled above/below the
        // current viewport during auto-scroll.
        function hitTestRows(): string[] {
            const el = scrollRef.current;
            const d  = drag.current;
            if (!el || !d) return [];

            const cr          = el.getBoundingClientRect();
            const curScrollTop = el.scrollTop;

            // Viewport Y → content Y
            const cs = d.startY - cr.top + d.startScrollTop;
            const ce = d.curY   - cr.top + curScrollTop;
            const contentTop    = Math.min(cs, ce);
            const contentBottom = Math.max(cs, ce);

            // Horizontal: viewport coords (container rarely scrolls sideways)
            const boxLeft  = Math.min(d.startX, d.curX);
            const boxRight = Math.max(d.startX, d.curX);

            const ids: string[] = [];
            const rows = el.querySelectorAll<HTMLElement>('tbody tr[data-device-id]');
            for (const row of rows) {
                const r = row.getBoundingClientRect();
                const rowTop    = r.top    - cr.top + curScrollTop;
                const rowBottom = r.bottom - cr.top + curScrollTop;

                if (
                    rowBottom > contentTop  &&
                    rowTop    < contentBottom &&
                    r.right   > boxLeft     &&
                    r.left    < boxRight
                ) {
                    const id = row.dataset.deviceId;
                    if (id) ids.push(id);
                }
            }
            return ids;
        }

        // Auto-scroll loop: runs while dragging and cursor is within ZONE px of
        // the top or bottom edge of the scroll container.
        function scrollLoop() {
            const el = scrollRef.current;
            const d  = drag.current;
            if (!el || !d?.active) { rafRef.current = null; return; }

            const cr   = el.getBoundingClientRect();
            const ZONE = 60;
            const my   = mouseYRef.current;

            if (my > cr.bottom - ZONE) {
                el.scrollTop += ((my - (cr.bottom - ZONE)) / ZONE) * 16;
            } else if (my < cr.top + ZONE) {
                el.scrollTop -= ((cr.top  + ZONE - my) / ZONE) * 16;
            }

            rafRef.current = requestAnimationFrame(scrollLoop);
        }

        function onMouseDown(e: MouseEvent) {
            if (e.button !== 0) return;
            if ((e.target as HTMLElement).closest('input,button,a,select,label')) return;

            const el = scrollRef.current;
            drag.current = {
                startX:         e.clientX,
                startY:         e.clientY,
                startScrollTop: el?.scrollTop ?? 0,
                curX:           e.clientX,
                curY:           e.clientY,
                active:         false,
            };
            mouseYRef.current = e.clientY;
        }

        function onMouseMove(e: MouseEvent) {
            const d = drag.current;
            if (!d) return;

            mouseYRef.current = e.clientY;
            d.curX = e.clientX;
            d.curY = e.clientY;

            if (!d.active) {
                if (Math.abs(d.curX - d.startX) < 5 && Math.abs(d.curY - d.startY) < 5) return;
                d.active = true;
                if (!rafRef.current) rafRef.current = requestAnimationFrame(scrollLoop);
            }

            showOverlay(d.startX, d.startY, d.curX, d.curY);
        }

        function onMouseUp() {
            const d = drag.current;

            if (d?.active) {
                const ids = hitTestRows();
                setSelectedRef.current?.(ids);
                // Swallow the click that browsers fire after mouseup so the
                // row-click toggle handler doesn't also fire.
                window.addEventListener('click', (ev) => ev.stopPropagation(), { once: true, capture: true });
            }

            drag.current = null;
            hideOverlay();
            if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
        }

        // Clear selection on a click that lands on blank page area outside the
        // scroll container. Runs at bubble phase so the drag-swallow capture
        // handler (stopPropagation) suppresses it after a rubber-band drag, and
        // button onClick handlers run before this fires so they still see the
        // current selection.
        //
        // Interacting with controls outside the table (the search/scan filter,
        // toolbar buttons, menus, dialogs) must NOT clear the selection — only a
        // click on genuinely empty space should. This mirrors the onMouseDown
        // ignore list so the two behaviours stay consistent.
        function onWindowClick(e: MouseEvent) {
            const el = scrollRef.current;
            if (!el || el.contains(e.target as Node)) return;
            const target = e.target as HTMLElement | null;
            if (
                target?.closest(
                    'input,button,a,select,label,textarea,[role="dialog"],[role="menu"],[data-keep-selection]',
                )
            ) {
                return;
            }
            setSelectedRef.current?.([]);
        }

        const container = scrollRef.current;
        container?.addEventListener('mousedown', onMouseDown);
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup',   onMouseUp);
        window.addEventListener('click',     onWindowClick);

        return () => {
            container?.removeEventListener('mousedown', onMouseDown);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup',   onMouseUp);
            window.removeEventListener('click',     onWindowClick);
            if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
            document.body.removeChild(overlay);
        };
    // All live state accessed through refs – empty deps intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}

// ---- Component ----

export function DeviceTable(props: Props): JSX.Element {
    const {
        sortedRows,
        columns,
        visibleMasterColumns,
        visibleExtraColumns,
        statusMap = {},
        tlsMap = {},
        selectedIds = [],
        allSelected = false,
        latestCommandMap = {},
        onlineMap,
        busyMap = {},
        onToggleSelectAll,
        onToggleSelectOne,
        onSetSelectedIds,
        onRowClick,
        onOpenDeviceDetails,
        requestSort,
        getSortIndicator,
        onCenterMapOnLocation,
        operatorScopedEventId,
        lifecycleMap = {},
        attributesMap = {},
        validationFailureMap = {},
        userIdToDisplayName = {},
    } = props;

    // Don't show busy/busy_from/busy_reason as separate columns; they're folded into the row.
    const effectiveExtraColumns = useMemo(
        () =>
            visibleExtraColumns.filter(
                (k) =>
                    !EXTRA_COLUMNS_HIDDEN_FROM_PICKER.has(k)
                    && k !== '__lifecycle_lost'
                    && k !== '__lifecycle_decommissioned'
                    && k !== '__lifecycle_damaged'
                    && k !== '__lifecycle_errored'
                    && k !== '__lifecycle_note',
            ),
        [visibleExtraColumns],
    );

    const scrollRef = useRef<HTMLDivElement>(null);
    useDragSelect({ scrollRef, onSetSelectedIds });

    if (!columns || columns.length === 0) {
        return (
            <div className="h-full overflow-x-auto overflow-y-auto rounded-lg shadow border p-4 text-sm text-gray-600 dark:text-slate-400">
                No devices to display.
            </div>
        );
    }

    const idKey = columns[0];
    const masterColumnsExcludingId = columns.slice(1);

    // O(1) per-row selection lookup instead of Array.includes (O(n) → O(n²) over the table).
    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    // Used to run a 1s ticker for elapsed-time display when any row is busy.
    const hasAnyBusy = useMemo(() => {
        if (!idKey) return false;

        for (const row of sortedRows) {
            const deviceId = (row[idKey] || '') as string;
            if (!deviceId) continue;

            const st = statusMap[deviceId];
            if ((busyMap[deviceId] ?? false) || getBusyFlag(st) > 0) return true;
        }

        return false;
    }, [sortedRows, idKey, statusMap, busyMap]);

    const [nowMs, setNowMs] = useState<number>(() => Date.now());

    /** Tick every second when any row is busy so elapsed timer updates. */
    useEffect(() => {
        if (!hasAnyBusy) return;

        setNowMs(Date.now());
        const t = setInterval(() => setNowMs(Date.now()), 1000);
        return () => clearInterval(t);
    }, [hasAnyBusy]);

    return (
        <div ref={scrollRef} className={`h-full ${TABLE_FRAME_CLASS} ${TABLE_SCROLL_CLASS} select-none`}>
            <style>{PROGRESS_STROBE_CSS}</style>
            <table className={TABLE_CLASS}>
                {/* Header: checkbox, id, master cols, then extra (online, fw, battery, last cmd, etc.) */}
                <thead className={TABLE_HEAD_STICKY_CLASS}>
                    <tr>
                        <th className={`${TABLE_HEAD_CELL_CLASS} w-10`}>
                            <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={onToggleSelectAll}
                            />
                        </th>

                        {idKey && (
                            <th
                                className={`${TABLE_HEAD_CELL_CLASS} cursor-pointer select-none`}
                                onClick={() => requestSort(idKey)}
                            >
                                {deviceMappingColumnLabel(idKey)} {getSortIndicator(idKey)}
                            </th>
                        )}

                        {masterColumnsExcludingId.map((col) =>
                            visibleMasterColumns.includes(col) ? (
                                <th
                                    key={col}
                                    className={`${TABLE_HEAD_CELL_CLASS} cursor-pointer select-none`}
                                    onClick={() => requestSort(col)}
                                >
                                    {deviceMappingColumnLabel(col)} {getSortIndicator(col)}
                                </th>
                            ) : null,
                        )}

                        {effectiveExtraColumns.map((key) => {
                            const label = extraColumnHeaderLabel(key);
                            if (!label) return null;
                            return (
                                <th
                                    key={key}
                                    className={`${TABLE_HEAD_CELL_CLASS} cursor-pointer select-none`}
                                    onClick={() => requestSort(key)}
                                >
                                    {label} {getSortIndicator(key)}
                                </th>
                            );
                        })}
                    </tr>
                </thead>

                {/* One row per device; selection, expand, status, latest command wired in */}
                <tbody className="text-gray-800 dark:text-slate-200">
                    {sortedRows.map((row, idx) => {
                        const deviceId = (idKey ? row[idKey] : '') as string;

                        const status = deviceId ? statusMap[deviceId] : undefined;
                        const isOnline = !!(deviceId && onlineMap && onlineMap[deviceId]);
                        const isBusy = !!(deviceId && busyMap && busyMap[deviceId]);

                        const hasTls =
                            deviceId &&
                            Object.prototype.hasOwnProperty.call(tlsMap, deviceId)
                                ? tlsMap[deviceId]
                                : undefined;

                        const selected = !!deviceId && selectedSet.has(deviceId);

                        const latestCommand = deviceId ? latestCommandMap[deviceId] : undefined;
                        const pairedId = deviceId
                            ? (pairedFrontIdFromRear(deviceId) ?? pairedRearIdFromFront(deviceId))
                            : null;
                        const pairCommandActive =
                            !!pairedId && isCommandActive(latestCommandMap[pairedId]?.status);
                        const lifecycleRow = deviceId ? lifecycleMap[deviceId] : undefined;
                        const attributesRow = deviceId ? attributesMap[deviceId] : undefined;
                        const validationFailureReasons = deviceId ? validationFailureMap[deviceId] : undefined;

                        return (
                            <DeviceTableRow
                                key={deviceId || `row-${idx}`}
                                row={row}
                                columns={columns}
                                visibleMasterColumns={visibleMasterColumns}
                                visibleExtraColumns={effectiveExtraColumns}
                                status={status}
                                forcedBusy={isBusy}
                                pairCommandActive={pairCommandActive}
                                tls={hasTls}
                                online={isOnline}
                                selected={selected}
                                latestCommand={latestCommand}
                                nowMs={nowMs}
                                onToggleSelect={() => onToggleSelectOne(deviceId)}
                                onRowClick={() => onRowClick(deviceId)}
                                onOpenDeviceDetails={() => onOpenDeviceDetails(deviceId)}
                                onCenterMapOnLocation={onCenterMapOnLocation}
                                operatorScopedEventId={operatorScopedEventId}
                                lifecycle={lifecycleRow}
                                attributes={attributesRow}
                                validationFailureReasons={validationFailureReasons}
                                userIdToDisplayName={userIdToDisplayName}
                            />
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
